import React from 'react';
import {
	AppBar,
	Toolbar,
	IconButton,
	Box,
	useTheme,
	useMediaQuery,
	Stack,
	Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { toggleOpen } from '@/features/sideNav/sideNavSlice';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import NotificationsMenu from '../NotificationsMenu';
import { TopNavCreateActions } from '@/components/buttons/TopNavCreateActions';
import { GlobalSearch } from '@/components/GlobalSearch';
import { AdminSearch } from '@/components/GlobalSearch/AdminSearch';
import { useAdminModeAccess } from '@/hooks/useAdminModeAccess';
import { AddMenuButton } from './AddMenuButton';
import { RecentClientsButton } from './RecentClientsButton';
import { AccountMenuButton } from './AccountMenuButton';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import { setThemeMode } from '@/features/userPrefs/userPrefsSlice';

const SEARCH_SX = {
	minWidth: 200,
	maxWidth: 560,
};

export const TopNavigationMenu = () => {
	const dispatch = useDispatch();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const isDarkMode = useSelector((state) => state.userPrefs.themeMode === 'dark');
	const handleToggleDarkMode = () => dispatch(setThemeMode(isDarkMode ? 'light' : 'dark'));

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const { getFeatureFlags } = useFeatureFlagContext();
	const showInAppNotifications = Boolean(getFeatureFlags('inAppNotifications')?.active);

	const { isAdminMode } = useAdminModeAccess();
	const showSearch = Boolean(!isMobile && userDetails);

	const handleDrawerToggle = () => {
		dispatch(toggleOpen());
	};

	return (
		<AppBar
			position='static'
			color='transparent'
			sx={{
				backgroundColor: 'background.paper',
				boxShadow: 'none',
				borderBottom: `1px solid ${theme.palette.divider}`,
				height: '48px',
			}}
		>
			<Toolbar
				variant='dense'
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 2,
					minWidth: 0,
					'& .MuiInputBase-root': {
						height: 32,
						padding: 0,
					},
					'& .MuiButtonBase-root': {
						height: 32,
						textTransform: 'none',
						whiteSpace: 'nowrap',
						flexShrink: 0,
					},
				}}
			>
				{showSearch && (
					<Box
						sx={{
							width: '100%',
							...SEARCH_SX,
						}}
					>
						{isAdminMode ? <AdminSearch /> : <GlobalSearch />}
					</Box>
				)}
				<Box
					sx={{
						flex: 1,
						minWidth: 0,
						display: 'flex',
						alignItems: 'center',
						overflow: 'hidden',
					}}
				>
					{isMobile && (
						<IconButton
							aria-label='open drawer'
							edge='start'
							onClick={handleDrawerToggle}
							sx={{ display: { sm: 'none' }, flexShrink: 0 }}
						>
							<MenuIcon />
						</IconButton>
					)}
				</Box>
				{userDetails && (
					<Stack direction='row' spacing={3} sx={{ flexShrink: 0 }}>
						{!isMobile && (
							<Stack direction='row' spacing={1} alignItems='center'>
								{!isAdminMode && (
									<>
										<TopNavCreateActions />
										<AddMenuButton />
										<RecentClientsButton />
									</>
								)}
								{showInAppNotifications && <NotificationsMenu />}
								<Tooltip title={`Toggle ${isDarkMode ? 'light' : 'dark'} mode on`}>
									<IconButton
										onClick={handleToggleDarkMode}
										aria-label='Toggle dark mode'
										size='small'
									>
										{isDarkMode ? (
											<LightModeOutlinedIcon color='primary' />
										) : (
											<DarkModeOutlinedIcon color='primary' />
										)}
									</IconButton>
								</Tooltip>
							</Stack>
						)}
						<AccountMenuButton />
					</Stack>
				)}
			</Toolbar>
		</AppBar>
	);
};
